import React from 'react'
import Chips from 'react-chips'

import {
  Row,
  Col,
  FormGroup,
  ControlLabel,
  FormControl,
  OverlayTrigger,
  Button,
  Popover,
  DropdownButton,
  MenuItem,
} from 'react-bootstrap'
import { RegionDropdown, CountryDropdown } from 'react-country-region-selector'
import propTypeSchema from 'react-json-schema-proptypes'

import Radium from 'radium'
import { Field, Typography } from '../types'
import { assetUrl, valueOrUndefined } from '../common'
import CustomChip from './custom-chip'
import { isEqual } from 'lodash'

const schema = {
  'type': 'object',
  'required': ['extraFields'],
  'properties': {
    'extraFields': { 'type': 'array', 'items': Field.Schema }
  }
}

@Radium
export default class ExtraFields extends React.PureComponent {
  static propSchema = schema
  static propTypes = propTypeSchema(schema, {
    required: [
      'extraFieldsValues',
      'extraFieldsDependencies',
      'onChangeValues',
      'onChangeDependencies',
    ],
    properties: {
      extraFieldsValues: {type: 'object'},
      extraFieldsDependencies: {type: 'object'},
      onChangeValues: {isFunction: true},
      onChangeDependencies: {isFunction: true},
      handleValidChange: {isFunction: true},
      displayErrors: {type: 'boolean'},
      astStyle: Typography.Schema,
      checkStyle: Typography.Schema,
    },
  })

  constructor(props) {
    super(props)
    this.state = {
      extraFieldsHasError: {},
      extraFieldsRequired: [],
      extraFieldsConditionallyRequired: {}
    }
  }

  hasValue = (fieldName) => {
    const {extraFieldsValues} = this.props
    return (
      Boolean(extraFieldsValues) &&
      Boolean(extraFieldsValues[fieldName]) &&
      extraFieldsValues[fieldName].length !== 0
    )
  }

  componentDidUpdate(prevProps) {
    const {displayErrors, extraFieldsValues} = this.props
    const {extraFieldsRequired, extraFieldsConditionallyRequired} = this.state

    if (
      extraFieldsRequired || extraFieldsConditionallyRequired
    ) {
      if (
        prevProps.displayErrors != displayErrors ||
        !isEqual(extraFieldsValues, prevProps.extraFieldsValues)
      ) {
        let withErrors = {}

        for (const required of extraFieldsRequired) {
          if (!this.hasValue(required)) {
            withErrors = {
              ...withErrors,
              [required]: true
            }
          }
        }

        for (const required in extraFieldsConditionallyRequired) {
          let actuallyRequired = extraFieldsConditionallyRequired[
            required
          ].every((optional) => this.hasValue(optional))
          if(actuallyRequired && !this.hasValue(required)){
            withErrors = {
              ...withErrors,
              [required]: true
            }
          }
        }
        this.props.handleValidChange(!this.allRequiredReady())
        if (displayErrors){
          this.setState({extraFieldsHasError: withErrors})
        }
      }
    }
    if (this.props.extraFieldsDependencies != prevProps.extraFieldsDependencies) {
      let required = this.state.extraFieldsRequired.slice()
      let conditionallyRequired = {...this.state.extraFieldsConditionallyRequired}
      let depFunc = this.dependenciesCompleted
      this.props.extraFields.forEach(function (field) {
        if (!depFunc(field.dependencies, field.optionalDependencies)) {
          var index = required.indexOf(field.name)
          if (index > -1) {
            required.splice(index, 1)
          }
          delete conditionallyRequired[field.name]
        }
      })

      this.setState({
        extraFieldsRequired: required,
        extraFieldsConditionallyRequired: conditionallyRequired
      }, () => {
        const hasErrors = !this.allRequiredReady()
        this.props.handleValidChange(hasErrors)
      })
    }
  }

  handleOnChangeEmailList = (chips, field) => {
    this.props.onChangeValues(
      {[field.name]: chips.slice(0, 10)},
      !this.allRequiredReady(),
      field.dependencies
    )
  }

  handleOnChangeField = (e, field) => {
    const value = e.target.value
    const name = field.name
    const fieldHasError = this.state.extraFieldsRequired.includes(name) ? (value == '') : false
    this.props.onChangeValues(
      {[name]: value},
      fieldHasError || !this.allRequiredReady(),
      field.dependencies
    )
  }

  handleOnChangeDropDown = (value, field) => {
    const name = field.name;
    const fieldHasError = this.state.extraFieldsRequired.includes(name)
      ? value === undefined
      : false
    this.props.onChangeValues(
      {[name]: value},
      fieldHasError || !this.allRequiredReady(),
      field.dependencies
    )
  }

  handleCountryRegionChange = (val, field) => {
    this.props.onChangeValues({[field.name]: val}, false, field.dependencies)
  }

  handleOnChangeCheckbox = (e, field) => {
    const {name, checked} = e.target
    this.props.onChangeDependencies(
      {[name]: checked},
      field,
      !this.allRequiredReady()
    )
  }

  addFieldToRequired = (name) => {
    const { extraFieldsRequired } = this.state
    if (!extraFieldsRequired.includes(name)) {
      const requiredList = extraFieldsRequired.slice()
      requiredList.push(name)
      this.setState({
        extraFieldsRequired: requiredList,
      }, () => {
        this.props.handleValidChange(true)
      })
    }
  }

  addFieldToConditionallyRequired = (field) => {
    const {extraFieldsConditionallyRequired} = this.state
    extraFieldsConditionallyRequired[field.name] = field.conditionallyRequired
    this.setState({extraFieldsConditionallyRequired})
  }

  allRequiredReady = () => {
    let allReady = true
    const { extraFieldsValues } = this.props
    const {extraFieldsRequired, extraFieldsConditionallyRequired} = this.state

    extraFieldsRequired.forEach(function (field) {
      if (!Boolean(extraFieldsValues[field])) {
        allReady = false
        return
      }
    })

    for (const required in extraFieldsConditionallyRequired) {
      let actuallyRequired = extraFieldsConditionallyRequired[required].every(optional => this.hasValue(optional))
      if(actuallyRequired && !this.hasValue(required)){
        allReady = false
      }
    }
    return allReady
  }

  isChecked = (field) => {
    const { extraFieldsDependencies } = this.props
    return extraFieldsDependencies ? extraFieldsDependencies[field.name] : false
  }

  dependenciesCompleted = (required, optionals) => {
    const reducer = (accumulator, currentValue) => (accumulator
      && Boolean(this.props.extraFieldsDependencies)
      && this.props.extraFieldsDependencies[currentValue])

    const optionalReducer = (accumulator, currentValue) => (accumulator
      || (Boolean(this.props.extraFieldsDependencies)
        && this.props.extraFieldsDependencies[currentValue]))

    const requiredReady = (required && required.reduce(reducer, true)) || !required
    const optionalsReady = (optionals && optionals.reduce(optionalReducer, false)) || !optionals

    return requiredReady && optionalsReady
  }

  renderExtraField = (field) => {
    if (
      !this.dependenciesCompleted(
        field.dependencies,
        field.optionalDependencies
      )
    ) {
      return
    }

    const { extraFieldsHasError } = this.state
    const { extraFieldsValues, displayErrors, astStyle, checkStyle, labelStyle } = this.props

    const styles = {
      checked: {
        ...checkStyle,
        backgroundImage: `url(${assetUrl('checkmark.png')}`
      },
      info: {
        fontFamily: "Open Sans",
        fontSize: "18px",
        lineHeight: "24px",
        color: "#303133",
        maxWidth: "328px",
        border: "1px solid rgba(151,151,151,0.14)",
        marginLeft: "-3px"
      },
      popoverTitle: {
        fontFamily: "Open Sans",
        fontSize: "18px",
        lineHeight: "24px",
        fontWeight: "bold",
        color: "#54585A",
        paddingBottom: "4px",
        paddingTop: "10px"
      },
      closeButtonStyle: {
        fontFamily: "Open Sans",
        fontWeight: "800",
        fontSize: "15px",
        lineHeight: "24px",
        padding: "0px",
        color: "#004B80",
        border: "none",
        margin: "-25px -5px 0px"
      },
      spanInfo: {
        color: "rgb(1, 179, 227)",
        fontWeight: "bold",
        fontSize: "14px"
      }
    }

    let infoOverlay = <div />

    if (field.required) {
      this.addFieldToRequired(field.name)
    }

    if (
      Boolean(field.conditionallyRequired) &&
      field.conditionallyRequired !== []
    ) {
      this.addFieldToConditionallyRequired(field)
    }

    if (field.withInfo) {
      infoOverlay = (
        <Popover style={styles.info} id="popover-basic">
          {field.infoTitle && <div style={styles.popoverTitle}>{field.infoTitle}</div>}
          {field.infoDesc}
        </Popover>)
    }

    let internalField = <div />

    const fieldWithErrors = displayErrors && extraFieldsHasError
    const requiredAst = field.required && (
      <span className={'astStyle'} style={astStyle}>
        {' '}
        *
      </span>
    )
    const fieldError = fieldWithErrors && extraFieldsHasError[field.name] && (
      <div className="formError" style={field.labelStyle}>
        &nbsp;Required
      </div>
    )
    const fieldClass =
      'infoEntry' +
      (extraFieldsHasError && extraFieldsHasError[field.name]
        ? ' has-error'
        : '')

    switch (field.type) {
      case 'textField':
        internalField = <FormControl
          id={field.name}
          name={field.name}
          type='text'
          onChange={(e) => this.handleOnChangeField(e, field)}
          value={valueOrUndefined(extraFieldsValues, field.name)}
          style={field.formStyle}
        />
        break
      case 'checkBox':
        internalField = <div style={field.checkBoxTextStyle}>
          {field.label && <ControlLabel style={field.labelStyle} className='checkBoxTitle'>{field.label}</ControlLabel>}
          <input
            id={field.name}
            name={field.name}
            type={field.type}
            onChange={(e) => this.handleOnChangeCheckbox(e, field)}
            checked={this.isChecked(field)}
          />
          <label htmlFor={field.name} style={this.isChecked(field) ? [styles.checked] : []} />
          <span className='checkbox'>{field.checkBoxText}
            {field.withInfo &&
              <OverlayTrigger trigger="hover" placement="bottom" overlay={infoOverlay}>
                <Button className={'infoStyle'} style={field.infoStyle}> i </Button>
              </OverlayTrigger>
            }
          </span>
        </div>
        break
      case 'emailList':
        internalField =
          (<div>
            <span style={styles.spanInfo}>Press enter to add each email</span>
            <Chips
              onChange={(chips) => this.handleOnChangeEmailList(chips, field)}
              value={valueOrUndefined(extraFieldsValues, field.name)}
              fromSuggestionsOnly={false}
              placeholder={'email@example.com'}
              renderChip={(value) => <CustomChip>{value}</CustomChip>}
              createChipKeys={[' ', 13, ',', ';', 9]}
            />
          </div>
        )
        break
      case 'state':
        internalField =
          <RegionDropdown
            id={field.name}
            name={field.name}
            country={valueOrUndefined(extraFieldsValues, 'recipientCountry') || 'United States'}
            value={valueOrUndefined(extraFieldsValues, field.name)}
            onChange={(e) => this.handleCountryRegionChange(e, field)}
            showDefaultOption={true}
            style={field.formStyle}
          />
        break
      case 'country':
        internalField =
          <CountryDropdown
            id={field.name}
            name={field.name}
            value={valueOrUndefined(extraFieldsValues, field.name) || 'United States'}
            onChange={(e) => this.handleCountryRegionChange(e, field)}
            showDefaultOption={false}
            style={field.formStyle}
          />
        break
      case 'textArea':
        internalField = <FormControl
          componentClass="textarea"
          rows="3"
          maxlength="400"
          id={field.name}
          name={field.name}
          onChange={(e) => this.handleOnChangeField(e, field)}
          value={valueOrUndefined(extraFieldsValues, field.name)}
          style={field.formStyle} />
        break
      case 'dropDown':
        if (valueOrUndefined(extraFieldsValues, field.name) === undefined) {
          this.handleOnChangeDropDown(field.options[0].value, field)
        }
        const toLabels = {}
        const options = field.options.map((option) => {
          toLabels[option.value] = option.label
          return <MenuItem eventKey={option.value}>{option.label}</MenuItem>
        })
        internalField = (
          <div>
            <DropdownButton
              title={toLabels[valueOrUndefined(extraFieldsValues, field.name)]}
              id="dropdown-menu-align-right"
              onSelect={(e) => this.handleOnChangeDropDown(e, field)}
            >
              {options}
            </DropdownButton>
          </div>
        )
        break
      default:
        console.warn(`unknown extra field type found: ${field.type}`)
        break
    }


    let customField = internalField

    if (field.type !== 'checkBox') {
      customField =
        <FormGroup
          className={fieldClass}>
          <ControlLabel style={field.labelStyle}>
            {field.label}
            {requiredAst}
            {field.withInfo && <span className={'infoStyle'} style={field.infoStyle}> i </span>}
          </ControlLabel>
          {fieldError}
          {internalField}
        </FormGroup>
    }

    return (
      <Col
        md={field.colDesktop ? field.colDesktop : 12}
        xs={field.colMobile ? field.colMobile : 12}
        key={field.name}
        style={fieldWithErrors ? field.colErrorStyle : field.colStyle}>
        {customField}
      </Col>
    )

  }

  render() {
    return (
      <div className='extraFieldsContainer'>
        <Row>
          {this.props.extraFields && this.props.extraFields.map(extraField =>
            this.renderExtraField(extraField)
          )}
        </Row>
      </div>
    )
  }
}

